<!-- src/components/student/StudentConf.vue -->
<template>
  <div class="conferences-section">
    <div class="section-container">
      <!-- HEADER -->
      <header class="section-header">
        <h2 class="section-title">Онлайн конференции</h2>

        <!-- Фильтр по категории -->
        <div class="category-filter" v-click-outside="closeCategoryDropdown">
          <button
            class="filter-button"
            @click="toggleCategoryDropdown"
            :class="{ active: showCategoryDropdown }"
          >
            <span class="filter-icon">📂</span>
            <span>{{ selectedCategoryLabel }}</span>
            <span class="dropdown-arrow" :class="{ rotated: showCategoryDropdown }">▼</span>
          </button>

          <transition name="dropdown">
            <div v-if="showCategoryDropdown" class="dropdown-menu">
              <button
                @click="selectCategory(null)"
                class="dropdown-item"
                :class="{ selected: selectedCategory === null }"
              >
                Все категории
              </button>

              <button
                v-for="category in availableCategories"
                :key="category.value"
                @click="selectCategory(category.value)"
                class="dropdown-item"
                :class="{ selected: category.value === selectedCategory }"
              >
                {{ category.label }}
              </button>
            </div>
          </transition>
        </div>

        <!-- Переключатель "В эфире" -->
        <label class="toggle-checkbox">
          <input type="checkbox" v-model="showLiveOnly" @change="onToggleLive" />
          <span class="toggle-label live">
            <span class="live-dot"></span>
            В эфире
          </span>
        </label>

        <!-- Переключатель прошедших -->
        <label class="toggle-checkbox">
          <input
            type="checkbox"
            v-model="localIncludePast"
            @change="onTogglePast"
          />
          <span class="toggle-label">Показать прошедшие</span>
        </label>

        <!-- Навигация слайдера -->
        <div class="slider-nav" v-if="showSliderNav">
          <button
            class="nav-btn"
            @click="slidePrev"
            :disabled="currentSlide === 0"
          >
            ‹
          </button>
          <span class="slide-counter">
            {{ currentSlide + 1 }} / {{ maxSlide + 1 }}
          </span>
          <button
            class="nav-btn"
            @click="slideNext"
            :disabled="currentSlide >= maxSlide"
          >
            ›
          </button>
        </div>
      </header>

      <!-- Состояние загрузки -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Загрузка конференций...</p>
      </div>

      <!-- Ошибка -->
      <div v-else-if="error" class="error-state">
        <div class="error-icon">⚠️</div>
        <p>{{ error }}</p>
        <button @click="$emit('retry')" class="retry-btn">Повторить</button>
      </div>

      <!-- Пустое состояние -->
      <div v-else-if="filteredCourses.length === 0" class="empty-state">
        <div class="empty-icon">📅</div>
        <h3 v-if="localIncludePast">Нет прошедших конференций</h3>
        <h3 v-else>Нет запланированных конференций</h3>
        <p v-if="showLiveOnly">Сейчас нет активных трансляций</p>
        <p v-else-if="localIncludePast">Завершённые конференции появятся здесь</p>
        <p v-else-if="selectedCategory">Нет конференций в выбранной категории</p>
        <p v-else>Конференции появятся, когда преподаватели их запланируют</p>
      </div>

      <!-- Слайдер карточек -->
      <div v-else class="cards-slider-wrapper">
        <div class="cards-slider">
          <div class="cards-track" :style="trackStyle">
            <article
              v-for="(courseGroup, index) in filteredCourses"
              :key="courseGroup.course_id"
              class="conf-card"
              :style="{ '--accent': getColor(index, courseGroup.course_id) }"
            >
              <!-- Заголовок карточки -->
              <header class="card-header">
                <div class="header-row">
                  <span class="color-bar"></span>
                  <h3 class="card-title">{{ courseGroup.course_title }}</h3>
                </div>
                <span v-if="courseGroup.course_category" class="card-category">
                  {{ translateCategory(courseGroup.course_category) }}
                </span>
              </header>

              <!-- Список дат -->
              <div
                class="dates-list"
                :class="{ scrollable: courseGroup.conferences.length > 3 }"
              >
                <div
                  v-for="conf in getSortedConferences(courseGroup.conferences)"
                  :key="conf.id"
                  class="date-row"
                  :class="getRowClass(conf)"
                >
                  <!-- Цветная точка -->
                  <span
                    class="status-dot"
                    :style="{ background: getDotColor(conf, index, courseGroup.course_id) }"
                    :class="getDotClass(conf)"
                  ></span>

                  <!-- Время и дата -->
                  <div class="time-block">
                    <time class="time">{{ formatTime(conf.scheduled_at) }}</time>
                    <span class="date">{{ formatDateShort(conf.scheduled_at) }}</span>
                  </div>

                  <!-- Название конференции -->
                  <span class="conf-name" :title="conf.title">{{ conf.title }}</span>

                  <!-- Кнопка действия -->
                  <button
                    v-if="shouldShowActionButton(conf)"
                    class="btn-action"
                    :class="getStatusClass(conf)"
                    @click="onJoinConference(conf)"
                  >
                    {{ getStatusLabel(conf) }}
                  </button>
                  <span
                    v-else
                    class="btn-status"
                    :class="getStatusClass(conf)"
                  >
                    {{ getStatusLabel(conf) }}
                  </span>
                </div>
              </div>

              <!-- Футер карточки -->
              <footer class="card-footer">
                <span class="conf-count">
                  {{ courseGroup.conferences.length }}
                  {{ pluralizeConf(courseGroup.conferences.length) }}
                </span>
                <router-link
                  :to="{ name: 'CoursePreview', params: { id: courseGroup.course_id } }"
                  class="course-link"
                >
                  К курсу →
                </router-link>
              </footer>
            </article>
          </div>
        </div>
      </div>

      <!-- Точки навигации -->
      <div class="dots-nav" v-if="showSliderNav">
        <button
          v-for="i in maxSlide + 1"
          :key="i"
          class="dot"
          :class="{ active: currentSlide === i - 1 }"
          @click="goToSlide(i - 1)"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
import { getCourseColor } from '@/utils/conferenceColors'

// Словарь переводов категорий
const CATEGORY_TRANSLATIONS = {
  // Технические
  tech: 'Технологии',
  technology: 'Технологии',
  programming: 'Программирование',
  development: 'Разработка',
  web: 'Веб-разработка',
  mobile: 'Мобильная разработка',
  devops: 'DevOps',
  '1c': '1С',
  
  // Бизнес
  business: 'Бизнес',
  sales: 'Продажи',
  marketing: 'Маркетинг',
  management: 'Менеджмент',
  finance: 'Финансы',
  hr: 'HR',
  
  // Дизайн
  design: 'Дизайн',
  ux: 'UX/UI',
  graphics: 'Графический дизайн',
  
  // Образование
  education: 'Образование',
  language: 'Языки',
  soft_skills: 'Soft Skills',
  
  // Прочее
  other: 'Другое',
  general: 'Общее',
}

// Палитра цветов (fallback)
const COLORS = [
  '#0096ed', '#00B894', '#E17055', '#0984E3', '#FDCB6E',
  '#E84393', '#00CEC9', '#FF7675', '#74B9FF', '#0096ed',
]

const JOIN_WINDOW_MINUTES = 30
const SOON_WINDOW_MINUTES = 60

export default {
  name: 'StudentConf',

  props: {
    /** Массив конференций студента */
    conferences: {
      type: Array,
      default: () => [],
    },
    /** Флаг загрузки */
    loading: {
      type: Boolean,
      default: false,
    },
    /** Сообщение об ошибке */
    error: {
      type: String,
      default: '',
    },
    /** Включить прошедшие конференции */
    includePast: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['join-conference', 'toggle-past', 'retry'],

  data() {
    return {
      localIncludePast: this.includePast,
      showLiveOnly: false,
      selectedCategory: null,
      showCategoryDropdown: false,
      
      // Слайдер
      currentSlide: 0,
      cardWidth: 380,
      visibleCards: 3,
    }
  },

  computed: {
    /** Группировка конференций по курсам */
    groupedConferences() {
      const groups = {}

      this.conferences.forEach(conf => {
        const courseId = conf.course_id || conf.courseId || conf.course?.id
        const courseTitle = conf.course_title || conf.course?.title || 'Курс'
        const courseCategory = conf.course_category || conf.course?.category || ''

        if (!courseId) return

        if (!groups[courseId]) {
          groups[courseId] = {
            course_id: courseId,
            course_title: courseTitle,
            course_category: courseCategory,
            conferences: [],
          }
        }
        groups[courseId].conferences.push(conf)
      })

      return Object.values(groups)
    },

    /** Доступные категории из конференций */
    availableCategories() {
      const categories = new Set()

      this.groupedConferences.forEach(course => {
        if (course.course_category) {
          categories.add(course.course_category)
        }
      })

      return Array.from(categories)
        .map(cat => ({
          value: cat,
          label: this.translateCategory(cat),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
    },

    /** Лейбл выбранной категории */
    selectedCategoryLabel() {
      if (!this.selectedCategory) return 'Все категории'
      return this.translateCategory(this.selectedCategory)
    },

    /** Отфильтрованные курсы */
    filteredCourses() {
      let courses = this.groupedConferences

      // Фильтр по категории
      if (this.selectedCategory) {
        courses = courses.filter(
          course => course.course_category === this.selectedCategory
        )
      }

      // Фильтр "только в эфире"
      if (this.showLiveOnly) {
        courses = courses
          .map(course => ({
            ...course,
            conferences: course.conferences.filter(conf => this.isLiveNow(conf)),
          }))
          .filter(course => course.conferences.length > 0)
      }

      return courses
    },

    /** Показывать навигацию слайдера */
    showSliderNav() {
      return this.filteredCourses.length > this.visibleCards
    },

    /** Максимальный индекс слайда */
    maxSlide() {
      return Math.max(0, this.filteredCourses.length - this.visibleCards)
    },

    /** Стиль трека слайдера */
    trackStyle() {
      return {
        transform: `translateX(-${this.currentSlide * this.cardWidth}px)`,
        transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }
    },
  },

  watch: {
    includePast(val) {
      this.localIncludePast = val
    },

    filteredCourses() {
      // Сброс позиции при изменении фильтров
      this.currentSlide = 0
    },
  },

  mounted() {
    this.updateVisibleCards()
    window.addEventListener('resize', this.updateVisibleCards)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateVisibleCards)
  },

  methods: {
    // ==================== ЦВЕТА И ПЕРЕВОДЫ ====================
    
    /** Получить цвет курса */
    getColor(index, courseId) {
      if (getCourseColor) {
        return getCourseColor(courseId, index)
      }
      // Fallback
      if (courseId) {
        const hash = String(courseId)
          .split('')
          .reduce((a, c) => a + c.charCodeAt(0), 0)
        return COLORS[hash % COLORS.length]
      }
      return COLORS[index % COLORS.length]
    },

    /** Перевод категории */
    translateCategory(category) {
      if (!category) return 'Без категории'
      const key = category.toLowerCase().trim()
      return CATEGORY_TRANSLATIONS[key] || category
    },

    // ==================== СТАТУСЫ КОНФЕРЕНЦИЙ ====================

    /**
     * Парсинг даты как локального времени
     * scheduled_at приходит без таймзоны (2025-12-21T02:16:00)
     * new Date() может интерпретировать это как UTC - исправляем
     */
    parseLocalDate(dateStr) {
      if (!dateStr) return new Date(NaN)
      // Если уже есть Z или +/- таймзона - парсим как есть
      if (/[Z+-]\d{2}:?\d{2}$/.test(dateStr) || dateStr.endsWith('Z')) {
        return new Date(dateStr)
      }
      // Иначе парсим как локальное время через компоненты
      const [datePart, timePart] = dateStr.split('T')
      if (!datePart) return new Date(NaN)
      const [year, month, day] = datePart.split('-').map(Number)
      const [hour = 0, minute = 0, second = 0] = (timePart || '00:00:00').split(':').map(Number)
      return new Date(year, month - 1, day, hour, minute, second)
    },

    /** Проверка: идёт ли сейчас */
    isLiveNow(conf) {
      // Если завершена - точно не в эфире
      if (
        conf.status === 'completed' ||
        conf.status === 'ended' ||
        conf.status === 'cancelled' ||
        conf.ended_at
      ) {
        return false
      }

      if (conf.status === 'live' || conf.status === 'in_progress') return true

      return Boolean(conf.started_at)
    },

    /** Проверка: завершена ли */
    isEnded(conf) {
      if (
        conf.status === 'completed' ||
        conf.status === 'ended' ||
        conf.status === 'cancelled' ||
        conf.ended_at
      ) {
        return true
      }

      if (this.isLiveNow(conf)) return false

      const now = Date.now()
      const scheduled = this.parseLocalDate(conf.scheduled_at).getTime()
      const duration = (conf.duration_minutes || 60) * 60 * 1000
      return now > scheduled + duration
    },

    /** Проверка: скоро начнётся (в течение часа) */
    isStartingSoon(conf) {
      if (this.isLiveNow(conf)) return false
      if (this.isEnded(conf)) return false
      if (conf.status === 'cancelled') return false

      const diff = this.parseLocalDate(conf.scheduled_at).getTime() - Date.now()
      const soonMs = SOON_WINDOW_MINUTES * 60 * 1000
      const joinMs = JOIN_WINDOW_MINUTES * 60 * 1000
      return diff <= soonMs && diff > joinMs
    },

    /** Проверка: можно присоединиться (за 30 минут) */
    canJoinSoon(conf) {
      if (this.isLiveNow(conf)) return false
      if (this.isEnded(conf)) return false
      if (conf.status === 'cancelled') return false
      
      const now = Date.now()
      const scheduled = this.parseLocalDate(conf.scheduled_at).getTime()
      const duration = (conf.duration_minutes || 60) * 60 * 1000
      const joinStart = scheduled - JOIN_WINDOW_MINUTES * 60 * 1000
      const joinEnd = scheduled + duration
      return now >= joinStart && now <= joinEnd
    },

    /** Текст статуса */
    getStatusLabel(conf) {
      if (this.isLiveNow(conf)) return 'В эфире'
      if (conf.status === 'cancelled') return 'Отменено'
      if (this.isEnded(conf)) return 'Завершено'
      if (this.canJoinSoon(conf)) return 'Войти'
      if (this.isStartingSoon(conf)) return 'Скоро'
      return 'Запланировано'
    },

    /** CSS класс статуса */
    getStatusClass(conf) {
      if (this.isLiveNow(conf)) return 'live'
      if (conf.status === 'cancelled') return 'cancelled'
      if (this.isEnded(conf)) return 'ended'
      if (this.canJoinSoon(conf)) return 'join'
      if (this.isStartingSoon(conf)) return 'soon'
      return 'scheduled'
    },

    /** Показывать кнопку действия (кликабельную) */
    shouldShowActionButton(conf) {
      return this.isLiveNow(conf) || this.canJoinSoon(conf)
    },

    // ==================== СОРТИРОВКА ====================

    /** Сортировка: live → скоро → будущие → завершённые/отменённые */
    getSortedConferences(conferences) {
      return [...conferences].sort((a, b) => {
        const getPriority = (conf) => {
          if (this.isLiveNow(conf)) return 0
          if (this.canJoinSoon(conf)) return 1
          if (this.isStartingSoon(conf)) return 2
          if (conf.status === 'cancelled') return 5
          if (this.isEnded(conf)) return 4
          // Будущие
          const scheduled = this.parseLocalDate(conf.scheduled_at).getTime()
          if (scheduled > Date.now()) return 3
          return 4
        }

        const priorityA = getPriority(a)
        const priorityB = getPriority(b)

        if (priorityA !== priorityB) {
          return priorityA - priorityB
        }

        // Внутри одного приоритета:
        // live/скоро/будущие — ближайшие первыми (asc)
        // прошедшие — недавние первыми (desc)
        if (priorityA <= 3) {
          return this.parseLocalDate(a.scheduled_at) - this.parseLocalDate(b.scheduled_at)
        }
        return this.parseLocalDate(b.scheduled_at) - this.parseLocalDate(a.scheduled_at)
      })
    },

    // ==================== ВИЗУАЛЬНЫЕ КЛАССЫ ====================

    /** Класс для строки */
    getRowClass(conf) {
      return {
        'is-live': this.isLiveNow(conf),
        'is-cancelled': conf.status === 'cancelled',
        'is-ended': this.isEnded(conf),
      }
    },

    /** Цвет точки */
    getDotColor(conf, index, courseId) {
      if (this.isLiveNow(conf)) return '#27ae60'
      if (conf.status === 'cancelled') return '#95a5a6'
      if (this.isEnded(conf)) return '#95a5a6'
      if (this.isStartingSoon(conf)) return '#f39c12'
      return this.getColor(index, courseId)
    },

    /** Класс для точки */
    getDotClass(conf) {
      if (this.isLiveNow(conf)) return 'pulse'
      if (conf.status === 'cancelled' || this.isEnded(conf)) return 'gray'
      if (this.isStartingSoon(conf)) return 'orange'
      return ''
    },

    // ==================== ФОРМАТИРОВАНИЕ ====================

    /** Форматирование времени */
    formatTime(dateStr) {
      if (!dateStr) return '--:--'
      return this.parseLocalDate(dateStr).toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    /** Короткий формат даты */
    formatDateShort(dateStr) {
      if (!dateStr) return ''
      const date = this.parseLocalDate(dateStr)
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      if (date.toDateString() === today.toDateString()) return 'Сегодня'
      if (date.toDateString() === tomorrow.toDateString()) return 'Завтра'

      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'short',
        weekday: 'short',
      })
    },

    /** Склонение слова "конференция" */
    pluralizeConf(n) {
      const abs = Math.abs(n) % 100
      const n1 = abs % 10
      if (abs > 10 && abs < 20) return 'конференций'
      if (n1 > 1 && n1 < 5) return 'конференции'
      if (n1 === 1) return 'конференция'
      return 'конференций'
    },

    // ==================== СЛАЙДЕР ====================

    /** Следующий слайд */
    slideNext() {
      if (this.currentSlide < this.maxSlide) {
        this.currentSlide++
      }
    },

    /** Предыдущий слайд */
    slidePrev() {
      if (this.currentSlide > 0) {
        this.currentSlide--
      }
    },

    /** Перейти к слайду */
    goToSlide(index) {
      this.currentSlide = Math.max(0, Math.min(index, this.maxSlide))
    },

    /** Адаптивное количество карточек */
    updateVisibleCards() {
      const width = window.innerWidth

      if (width >= 1700) {
        this.visibleCards = 4
        this.cardWidth = 380
      } else if (width >= 1300) {
        this.visibleCards = 3
        this.cardWidth = 380
      } else if (width >= 900) {
        this.visibleCards = 2
        this.cardWidth = 380
      } else {
        this.visibleCards = 1
        this.cardWidth = 340
      }

      // Корректируем текущий слайд
      if (this.currentSlide > this.maxSlide) {
        this.currentSlide = Math.max(0, this.maxSlide)
      }
    },

    // ==================== ФИЛЬТРЫ ====================

    /** Переключить dropdown категорий */
    toggleCategoryDropdown() {
      this.showCategoryDropdown = !this.showCategoryDropdown
    },

    /** Закрыть dropdown */
    closeCategoryDropdown() {
      this.showCategoryDropdown = false
    },

    /** Выбрать категорию */
    selectCategory(category) {
      this.selectedCategory = category
      this.showCategoryDropdown = false
      this.currentSlide = 0
    },

    /** Переключение прошедших */
    onTogglePast() {
      // Сбрасываем "В эфире" при выборе прошедших
      if (this.localIncludePast) {
        this.showLiveOnly = false
      }
      this.$emit('toggle-past', this.localIncludePast)
    },

    /** Переключение "В эфире" */
    onToggleLive() {
      // Сбрасываем "Показать прошедшие" при выборе "В эфире"
      if (this.showLiveOnly && this.localIncludePast) {
        this.localIncludePast = false
        this.$emit('toggle-past', false)
      }
    },

    // ==================== ДЕЙСТВИЯ ====================

    /** Присоединиться к конференции */
    onJoinConference(conf) {
      this.$emit('join-conference', conf)
    },
  },

  directives: {
    'click-outside': {
      mounted(el, binding) {
        el._clickOutside = event => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutside)
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside)
      },
    },
  },
}
</script>

<style scoped>
/* ============================================
   ОСНОВНОЙ КОНТЕЙНЕР
   ============================================ */
.conferences-section {
  width: 100%;
  padding: 20px 0;
}

.section-container {
  max-width: 1560px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ============================================
   HEADER
   ============================================ */
.section-header {
  display: flex;
  align-items: center;
  /* justify-content: center; */
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
  margin-left: -70px;
}

.section-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0;
  color: #252641;
}

/* ============================================
   ФИЛЬТР КАТЕГОРИЙ
   ============================================ */
.category-filter {
  position: relative;
}

.filter-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 15px;
}

.filter-button:hover,
.filter-button.active {
  border-color: #0096ed;
  background: #eaf6ff;
}

.filter-icon {
  font-size: 14px;
}

.dropdown-arrow {
  font-size: 10px;
  transition: transform 0.2s;
  color: #888;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 200px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 100;
  overflow: hidden;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 12px 18px;
  text-align: left;
  border: none;
  background: none;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 14px;
}

.dropdown-item:hover {
  background: #f5f5f5;
}

.dropdown-item.selected {
  background: #eaf6ff;
  color: #0096ed;
  font-weight: 500;
}

/* ============================================
   ЧЕКБОКСЫ-ПЕРЕКЛЮЧАТЕЛИ
   ============================================ */
.toggle-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox input {
  display: none;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #f5f5f5;
  border-radius: 10px;
  font-size: 14px;
  color: #555;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.toggle-checkbox input:checked + .toggle-label {
  background: #eaf6ff;
  color: #0096ed;
  border-color: #0096ed;
}

.toggle-checkbox:hover .toggle-label {
  background: #ececec;
}

.toggle-checkbox input:checked + .toggle-label:hover {
  background: #eaf6ff;
}

/* "В эфире" */
.toggle-label.live {
  gap: 8px;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #aaa;
  border-radius: 50%;
  transition: all 0.2s;
}

.toggle-checkbox input:checked + .toggle-label.live {
  background: #e8f5e9;
  color: #27ae60;
  border-color: #27ae60;
}

.toggle-checkbox input:checked + .toggle-label.live .live-dot {
  background: #27ae60;
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.3);
    opacity: 0.7;
  }
}

/* ============================================
   НАВИГАЦИЯ СЛАЙДЕРА
   ============================================ */
.slider-nav {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #e0e0e0;
  background: #fff;
  font-size: 22px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}

.nav-btn:hover:not(:disabled) {
  background: #0096ed;
  border-color: #0096ed;
  color: #fff;
}

.nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.slide-counter {
  font-size: 14px;
  color: #666;
  min-width: 60px;
  text-align: center;
  font-weight: 500;
}

/* ============================================
   СОСТОЯНИЯ
   ============================================ */
.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #f0f0f0;
  border-top-color: #0096ed;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state {
  color: #e74c3c;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.retry-btn {
  margin-top: 16px;
  padding: 12px 32px;
  background: #0096ed;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 16px;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #0096ed;
}

.empty-state .empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.empty-state h3 {
  margin: 0 0 12px;
  color: #333;
  font-size: 22px;
}

.empty-state p {
  color: #666;
  margin: 0;
  font-size: 16px;
}

/* ============================================
   СЛАЙДЕР КАРТОЧЕК
   ============================================ */
.cards-slider-wrapper {
  padding: 20px 0;
  margin: -20px 0;
}

.cards-slider {
  overflow: visible;
  clip-path: inset(-20px 0 -20px 0);
}

.cards-track {
  display: flex;
  gap: 20px;
  justify-content: center;
  padding: 10px 0;
}

/* Точки навигации */
.dots-nav {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 28px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: #ddd;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s;
}

.dot:hover {
  background: #bbb;
  transform: scale(1.2);
}

.dot.active {
  background: #0096ed;
  width: 28px;
  border-radius: 5px;
}

/* ============================================
   КАРТОЧКА КОНФЕРЕНЦИИ
   ============================================ */
.conf-card {
  width: 360px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
  min-height: 320px;
}

.conf-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

/* Заголовок */
.card-header {
  padding: 20px 24px;
  background: linear-gradient(
    135deg,
    var(--accent, #0096ed) 0%,
    color-mix(in srgb, var(--accent, #0096ed) 70%, white) 100%
  );
  color: #fff;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-bar {
  width: 4px;
  height: 28px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 2px;
}

.card-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-category {
  display: inline-block;
  margin-top: 10px;
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
}

/* Список дат */
.dates-list {
  padding: 16px 20px;
  max-height: 210px;
  flex: 1;
}

.dates-list.scrollable {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #ddd transparent;
}

.dates-list.scrollable::-webkit-scrollbar {
  width: 5px;
}

.dates-list.scrollable::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}

/* Строка с датой */
.date-row {
  display: grid;
  grid-template-columns: 14px 90px 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;
}

.date-row:last-child {
  border-bottom: none;
}

.date-row.is-live {
  background: linear-gradient(90deg, rgba(39, 174, 96, 0.1) 0%, transparent 100%);
  margin: 0 -20px;
  padding: 14px 20px;
  border-radius: 10px;
  border-bottom: none;
}

.date-row.is-cancelled,
.date-row.is-ended {
  opacity: 0.5;
}

/* Точка статуса */
.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.pulse {
  animation: pulse 1.5s ease-in-out infinite;
}

.status-dot.gray {
  background: #95a5a6 !important;
}

.status-dot.orange {
  background: #f39c12 !important;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

/* Время */
.time-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  min-width: 90px;
}

.time {
  font-size: 15px;
  font-weight: 600;
  color: #636363;
}

.date {
  font-size: 12px;
  color: #888;
}

/* Название */
.conf-name {
  flex: 1;
  font-size: 13px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* ============================================
   КНОПКИ ДЕЙСТВИЙ
   ============================================ */
.btn-action {
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s;
}

/* В эфире */
.btn-action.live {
  background: #27ae60;
  color: #fff;
  animation: pulse-btn 2s ease-in-out infinite;
}

.btn-action.live:hover {
  background: #219a52;
  transform: scale(1.05);
}

/* Войти */
.btn-action.join {
  background: #27ae60;
  color: #fff;
}

.btn-action.join:hover {
  background: #219a52;
  transform: scale(1.05);
}

@keyframes pulse-btn {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(39, 174, 96, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(39, 174, 96, 0);
  }
}

/* Некликабельные статусы */
.btn-status {
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-status.scheduled {
  background: #f0f0f0;
  color: #888;
}

.btn-status.soon {
  background: #fffbeb;
  color: #b45309;
}

.btn-status.ended {
  background: #f5f5f5;
  color: #aaa;
}

.btn-status.cancelled {
  background: #fff5f5;
  color: #e74c3c;
}

/* Футер карточки */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
  margin-top: auto;
}

.conf-count {
  font-size: 13px;
  color: #888;
}

.course-link {
  font-size: 13px;
  color: #0096ed;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.course-link:hover {
  color: #0096ed;
  text-decoration: underline;
}

/* ============================================
   АНИМАЦИИ
   ============================================ */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

/* ============================================
   АДАПТИВНОСТЬ
   ============================================ */
@media (max-width: 1200px) {
  .cards-track {
    justify-content: flex-start;
    padding-left: 20px;
  }
}

@media (max-width: 900px) {
  .section-header {
    padding: 0 10px;
  }

  .conf-card {
    width: 340px;
  }
}

@media (max-width: 768px) {
  .section-container {
    padding: 0 16px;
  }

  .section-title {
    font-size: 24px;
    width: 100%;
    text-align: center;
  }

  .section-header {
    flex-direction: column;
    gap: 12px;
  }

  .conf-card {
    width: 320px;
  }

  .date-row {
    grid-template-columns: 12px 80px 1fr;
  }

  .conf-name {
    display: none;
  }

  .btn-action,
  .btn-status {
    grid-column: 2 / -1;
    justify-self: start;
    margin-top: 6px;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 22px;
  }

  .filter-button,
  .toggle-label {
    font-size: 13px;
    padding: 8px 14px;
  }

  .conf-card {
    width: 290px;
  }

  .card-header {
    padding: 16px 18px;
  }

  .card-title {
    font-size: 15px;
  }
}
</style>
